import {
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  Input,
  Label,
  ModalBody,
  Row,
} from "reactstrap";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import {
  CLUSTER_QUERY_KEY,
  VIEW_CLUSTER_QUERY_KEY,
  clusterService,
} from "../../../../../Services/clusterService";
import {
  REGION_QUERY_KEY,
  VIEW_REGION_QUERY_KEY,
  regionService,
} from "../../../../../Services/regionsService";
import { useState } from "react";
import { toast } from "react-toastify";
import { Btn } from "../../../../../AbstractElements";
import { useAppSelector } from "../../../../../ReduxToolkit/Hooks";
import { Region } from "../../../../../Types/MasterData/Region";
import { VIEW_DEPSITE_QUERY_KEY } from "../../../../../Services/deploymentsiteService";

const INITIAL_STATE = {
  company: "",
  region: 0,
  name: "",
  status: -1,
  created_by: undefined,
  updated_by: undefined,
};

interface CreateFormProps {
  toggle?: () => void;
  company_code: string; // required
}

const CreateClusterForm: React.FC<CreateFormProps> = ({
  company_code,
  toggle,
}) => {
  const queryClient = useQueryClient();
  const user = useAppSelector((state) => state.auth.user);
  const [formData, setFormData] = useState(INITIAL_STATE);

  const createMutation = useMutation({
    mutationFn: clusterService.createCluster,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VIEW_CLUSTER_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: VIEW_DEPSITE_QUERY_KEY });
      toast.success("Cluster has been created successfully.");

      setFormData(INITIAL_STATE);

      if (toggle) {
        toggle();
      }
    },
    onError: (error: any) => {
      const responseData = error.response?.data;

      if (responseData?.errors) {
        const firstErrorKey = Object.keys(responseData.errors)[0];
        const errorMessage = responseData.errors[firstErrorKey][0];
        toast.error(errorMessage);
      } else if (responseData?.message) {
        toast.error(responseData.message);
      } else {
        toast.error(
          "Unable to create cluster. Please verify the entered information.",
        );
      }
    },
  });

  const { data: regionData, isLoading: isLoading } = useQuery<{
    data: Region[];
  }>({
    queryKey: REGION_QUERY_KEY,
    queryFn: regionService.getAllRegions,
  });

  const regions: Region[] =
    regionData?.data?.filter((item) => item.status === 1) ?? [];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSelectedStatus = (status: number) => {
    setFormData((prev) => ({ ...prev, status: status }));
  };

  const handleSelectedRegion = (region: number) => {
    setFormData((prev) => ({ ...prev, region: region }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.status === -1) {
      toast.warn("Please select a Status");
      return;
    }

    if (!user) {
      toast.error("User not authenticated");
      return;
    }

    const payload = {
      ...formData,
      company_code: company_code,
    };
    createMutation.mutate(payload);
  };

  return (
    <ModalBody>
      <Form className="needs-validation" onSubmit={handleSubmit}>
        <Row className="g-3">
          <Col md="12">
            <Label>
              Region<b className="text-danger">*</b>
            </Label>
            <Input
              value={formData.region}
              id="region"
              name="region"
              type="select"
              disabled={isLoading}
              onChange={(e) => handleSelectedRegion(Number(e.target.value))}
            >
              <option value="" selected disabled>
                {isLoading ? "Loading..." : "Select Region"}
              </option>
              {!isLoading &&
                regions.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
            </Input>
          </Col>
          <Col md="12">
            <Label>
              Name<b className="text-danger">*</b>
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="Enter New Cluster"
              maxLength={150}
              minLength={3}
              required
              value={formData.name}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="12">
            <Label>
              Status<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-2"
              value={formData.status}
              id="status"
              name="status"
              type="select"
              onChange={(e) => handleSelectedStatus(Number(e.target.value))}
            >
              <option disabled value={-1}>
                Select Status
              </option>
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </Input>
          </Col>

          <Col xs="12">
            <Btn
              color="primary"
              className="w-100"
              type="submit"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? "Creating Cluster..." : "Create"}
            </Btn>
          </Col>
        </Row>
      </Form>
    </ModalBody>
  );
};

export default CreateClusterForm;
